type SBSound = Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.BaseSound;

export interface AudioChannel {
  isMuted: boolean;
  isPaused: boolean;
  isSingleTrack: boolean;
  singleTrack?: Phaser.Sound.BaseSound;
  tracksWebAudio: Map<string, AudioTrack<Phaser.Sound.WebAudioSound>>;
  tracksHTML5Audio: Map<string, AudioTrack<Phaser.Sound.HTML5AudioSound>>;
  tracksBaseSound: Map<string, AudioTrack<Phaser.Sound.BaseSound>>;
}

export interface AudioTrack<T> {
  reference: string;
  sound: T;
}

export class AudioHandler {
  channels: { [key: string]: AudioChannel };

  constructor() {
    this.channels = {};
  }

  public makeChannel(channelId: string, isSingleTrack: boolean = false) {
    this.findOrMakeChannel(channelId, isSingleTrack);
  }

  public getChannelMute(channelId: string): boolean {
    return this.channels?.[channelId].isMuted;
  }

  public setChannelMute(channelId: string, isMuted: boolean) {
    console.log(`${isMuted ? 'Muting' : 'Unmuting'} audio for ${channelId}`);
    if (this.channels[channelId] && this.channels[channelId].isMuted != isMuted) {
      this.channels[channelId].isMuted = isMuted;
      this.executeOnSubChannel(this.channels[channelId].tracksWebAudio, (track: AudioTrack<SBSound>) => this.setSoundMute(track, isMuted));
      this.executeOnSubChannel(this.channels[channelId].tracksHTML5Audio, (track: AudioTrack<SBSound>) => this.setSoundMute(track, isMuted));
    }
  }

  public setChannelPause(channelId: string, isPaused: boolean) {
    if (this.channels[channelId] && this.channels[channelId].isPaused != isPaused) {
      this.channels[channelId].isPaused = isPaused;
      this.executeOnSubChannel(this.channels[channelId].tracksWebAudio, (track: AudioTrack<SBSound>) => this.setSoundPause(track, isPaused));
      this.executeOnSubChannel(this.channels[channelId].tracksHTML5Audio, (track: AudioTrack<SBSound>) => this.setSoundPause(track, isPaused));
    }
  }

  public playSound(scene: Phaser.Scene, audioId: string, channelId: string, config?: Phaser.Types.Sound.SoundConfig): [reference: string, success: boolean] {
    const channel: AudioChannel = this.findOrMakeChannel(channelId);

    let reference: string = audioId + Math.random().toString(16).slice(2);

    // If we're already playing this track and were're in single track mode then we can skip.
    if (channel.isSingleTrack && audioId == channel?.singleTrack?.key) return ['', false];

    try {
      let sound = scene.sound.add(audioId, config);

      if (channel.isSingleTrack) {
        this.stopAndRemoveAllTracks(channel.tracksWebAudio);
        this.stopAndRemoveAllTracks(channel.tracksHTML5Audio);
        this.stopAndRemoveAllTracks(channel.tracksBaseSound);

        channel.singleTrack = sound;
      } else {
      }

      sound.on(Phaser.Sound.Events.COMPLETE, this.onTrackComplete, this);

      if (sound instanceof Phaser.Sound.WebAudioSound) {
        if (channel.isMuted) sound.setMute(true);
        if (channel.isSingleTrack) sound.setLoop(true);
        channel.tracksWebAudio.set(reference, { reference: reference, sound: sound });
      } else if (sound instanceof Phaser.Sound.HTML5AudioSound) {
        if (channel.isMuted) sound.setMute(true);
        if (channel.isSingleTrack) sound.setLoop(true);
        channel.tracksHTML5Audio.set(reference, { reference: reference, sound: sound });
      } else {
        channel.tracksBaseSound.set(reference, { reference: reference, sound: sound });
      }

      sound.play();
      // console.log(channelId, channel);
      return [reference, true];
    } catch (error) {
      console.warn(`Failed to play sound ${audioId}`);
    }

    return [reference, true];
  }

  public stopAndRemoveSound(reference: string, channelId: string) {
    let channel = this.channels[channelId];

    this.stopAndRemoveTrack(reference, channel.tracksWebAudio);
    this.stopAndRemoveTrack(reference, channel.tracksHTML5Audio);
    this.stopAndRemoveTrack(reference, channel.tracksBaseSound);
  }

  protected stopAndRemoveTrack(reference: string, tracks: Map<string, AudioTrack<Phaser.Sound.BaseSound>>): boolean {
    if (tracks.has(reference)) {
      let track = tracks.get(reference);
      track?.sound.stop();
      track?.sound.destroy();
      tracks.delete(reference);
      return true;
    }

    return false;
  }

  protected stopAndRemoveAllTracks(tracks: Map<string, AudioTrack<Phaser.Sound.BaseSound>>) {
    tracks.forEach((track, key) => {
      track.sound.stop();
      track.sound.destroy();
    });

    tracks.clear();
  }

  protected onTrackComplete(sound: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound) {
    // console.log(`Audio track ${sound.key} complete!`);

    let wasRemoved = this.findAndRemoveTrackInChannels(sound);
  }

  // protected findTrackInChannels(
  //   sound: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.BaseSound
  // ): [channel: AudioChannel | undefined, track: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.BaseSound | undefined] {
  //   Object.keys(this.channels).forEach((channelId) => {
  //     let channel = this.channels[channelId];
  //     let result = this.findTrackInChannel(channel, sound);
  //     if (result) return [channel, result];
  //   });

  //   return [undefined, undefined];
  // }

  // protected findTrackInChannel(
  //   channel: AudioChannel,
  //   sound: Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.BaseSound
  // ): Phaser.Sound.WebAudioSound | Phaser.Sound.HTML5AudioSound | Phaser.Sound.BaseSound | undefined {
  //   let result = channel.tracksWebAudio.find((track) => track == sound);
  //   if (result) return result;

  //   let result1 = channel.tracksHTML5Audio.find((track) => track == sound);
  //   if (result1) return result1;

  //   let result2 = channel.tracksBaseSound.find((track) => track == sound);
  //   if (result2) return result2;

  //   return undefined;
  // }

  protected findAndRemoveTrackInChannels(sound: SBSound): boolean {
    let result = false;

    Object.keys(this.channels).forEach((channelId) => {
      let channel = this.channels[channelId];
      let result = this.findAndRemoveTrackInChannel(channel, sound);
      if (result) return result;
    });

    return result;
  }

  protected findAndRemoveTrackInChannel(channel: AudioChannel, sound: SBSound): boolean {
    let reference = '';
    channel.tracksWebAudio.forEach((track) => {
      if (track.sound == sound) {
        track.sound.stop();
        track.sound.destroy();
        reference = track.reference;
      }
    });
    if (reference !== '') {
      channel.tracksWebAudio.delete(reference);
      return true;
    }

    reference = '';
    channel.tracksHTML5Audio.forEach((track) => {
      if (track.sound == sound) {
        track.sound.stop();
        track.sound.destroy();
        reference = track.reference;
      }
    });
    if (reference !== '') {
      channel.tracksHTML5Audio.delete(reference);
      return true;
    }

    reference = '';
    channel.tracksBaseSound.forEach((track) => {
      if (track.sound == sound) {
        track.sound.stop();
        track.sound.destroy();
        reference = track.reference;
      }
    });
    if (reference !== '') {
      channel.tracksBaseSound.delete(reference);
      return true;
    }

    return false;
  }

  protected executeOnSubChannel(subChannel: Map<string, AudioTrack<SBSound>>, command: (track: AudioTrack<SBSound>) => void) {
    subChannel.forEach((track) => {
      command?.(track);
    });
  }

  protected setSoundMute(track: AudioTrack<SBSound>, isMuted: boolean) {
    if (track.sound instanceof Phaser.Sound.WebAudioSound || track.sound instanceof Phaser.Sound.HTML5AudioSound) {
      track.sound.setMute(isMuted);
    }
  }

  protected setSoundPause(track: AudioTrack<SBSound>, isPaused: boolean) {
    if (isPaused && !track.sound.isPaused) track.sound.pause();
    else if (!isPaused && track.sound.isPaused) track.sound.resume();
  }

  protected findOrMakeChannel(channelId: string, isSingleTrack: boolean = false) {
    if (!this.channels[channelId]) {
      this.channels[channelId] = {
        isMuted: false,
        isPaused: false,
        isSingleTrack: isSingleTrack,
        tracksWebAudio: new Map(),
        tracksHTML5Audio: new Map(),
        tracksBaseSound: new Map()
      };
    }

    return this.channels[channelId];
  }
}
